<template>
  <div v-if="isEnglish">
    <!-- Cookie banner -->
    <div id="cookieConsentBanner" class="cookie-banner" v-show="showBanner">
      <button
        id="cookieBannerClose"
        aria-label="Close cookie banner"
        class="cookie-banner-close"
        @click="agreeAllAndCloseBanner"
      >
        ×
      </button>
      <div class="cookie-banner-container">
        <span>
          We use cookies and similar technologies to personalize content and ads
          and analyze our traffic. By browsing our site or by selecting "X", you
          agree to our use of cookies and our sharing of information with our
          partners. View the Lenovo
          <a
            id="cookiePrivacyLink"
            :href="privacyUrl"
            target="_blank"
            class="link-primary"
            >Website Privacy Statement</a
          >. Visit our
          <span
            class="cookie-consent-link link-primary"
            @click="openCookieConsent"
            >Cookie Consent Tool</span
          >
          to manage your preferences.
          <span
            id="rejectNonEssentialCookies"
            class="link-primary"
            @click="rejectAllAndCloseBanner"
            >Click Here</span
          >
          to Reject All non-essential cookies.
        </span>
      </div>
    </div>

    <!-- Cookie Preferences Modal -->
    <div
      id="cookieConsentModalOverlay"
      class="cookie-modal-overlay"
      v-show="showModal"
      @click.self="handleOverlayClick"
    >
      <div id="cookieConsentModal" class="cookie-modal">
        <!-- Top Bar with Go Back (hidden by default, shown for Analytics/Google Analytics view) -->
        <div
          id="cookieModalTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'analytics'"
        >
          <a
            id="analyticsGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showMainView"
            >Go Back</a
          >
          <button
            id="cookiePrefCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Google Analytics Detail (hidden by default) -->
        <div
          id="googleAnalyticsTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'googleAnalytics'"
        >
          <a
            id="googleAnalyticsGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showAnalyticsView"
            >< Back to Analytics</a
          >
          <button
            id="googleAnalyticsCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Essential Detail (hidden by default) -->
        <div
          id="essentialTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'essential'"
        >
          <a
            id="essentialGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showMainView"
            >Go Back</a
          >
          <button
            id="essentialCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Akamai Technologies Detail -->
        <div
          id="akamaiTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'akamai'"
        >
          <a
            id="akamaiGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showEssentialView"
            >< Back to Essential</a
          >
          <button
            id="akamaiCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Font Awesome Detail -->
        <div
          id="fontAwesomeTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'fontAwesome'"
        >
          <a
            id="fontAwesomeGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showEssentialView"
            >< Back to Essential</a
          >
          <button
            id="fontAwesomeCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Google Fonts Detail -->
        <div
          id="googleFontsTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'googleFonts'"
        >
          <a
            id="googleFontsGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showEssentialView"
            >< Back to Essential</a
          >
          <button
            id="googleFontsCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Top Bar for Google reCAPTCHA Detail -->
        <div
          id="googleRecaptchaTopBar"
          class="cookie-modal-topbar"
          v-show="currentView === 'googleRecaptcha'"
        >
          <a
            id="googleRecaptchaGoBackLink"
            href="javascript:void(0);"
            class="cookie-modal-topbar-link"
            @click="showEssentialView"
            >< Back to Essential</a
          >
          <button
            id="googleRecaptchaCloseTop"
            class="cookie-modal-topbar-close"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <!-- Scrollable Content Area -->
        <div id="cookieConsentModalContent" class="cookie-modal-content">
          <!-- Main Content (default view) -->
          <div
            id="cookiePrefMainContent"
            class="cookie-pref-main-content"
            v-show="currentView === 'main'"
          >
            <div class="text-center">
              <div class="cookie-logo-img">
                <img
                  src="@/assets/lenovo_logo.svg"
                  height="40px"
                  alt="Lenovo"
                />
              </div>
            </div>
            <div class="cookie-title-main">
              Information We Collect About You
            </div>
            <div class="cookie-info-section-left">
              We want to be transparent about the data we and our partners
              collect and how we use it, so you can best exercise control over
              your personal data. For more information, please see our
              <a
                :href="privacyUrl"
                target="_blank"
                class="cookie-category-title"
                >Privacy Policy</a
              >.
            </div>
            <div class="cookie-subtitle">Information Our Partners Collect</div>
            <div class="cookie-info-section-mb16">
              We use the following partners to better improve your overall web
              browsing experience. They use cookies and other mechanisms to
              connect you with your social networks and tailor advertising to
              better match your interests. You can elect to opt-out of this
              information collection by unticking the boxes below.
            </div>
            <div class="mb-17">
              <div class="cookie-vendor-item-mb9">
                <span
                  id="analyticsTitleLink"
                  class="cookie-category-title"
                  @click="showAnalyticsView"
                  style="cursor: pointer;"
                  >Analytics</span
                >
                <span class="cookie-switch">
                  <input
                    id="cookiePrefAnalytics"
                    type="checkbox"
                    v-model="cookiePrefs.analytics"
                    @change="onSwitchChange"
                  />
                  <label for="cookiePrefAnalytics"></label>
                </span>
              </div>
              <div class="cookie-category-description">
                We use analytics to improve our website by better understanding
                how often users visit the site, what pages they visit most, and
                how long they spend on our site.
              </div>
              <div class="cookie-vendor-item-mb9">
                <span
                  id="essentialTitleLink"
                  class="cookie-category-title"
                  @click="showEssentialView"
                  style="cursor: pointer;"
                  >Essential</span
                >
                <span class="cookie-required-label">Required</span>
              </div>
              <div class="cookie-category-description-last">
                We rely on cookies, javascript, and other web technologies to
                serve key—or essential—elements on the site.
              </div>
            </div>
          </div>
          <button
            id="cookiePrefClose"
            class="cookie-modal-close"
            @click="handleCloseClick"
          >
            ×
          </button>

          <!-- Analytics Detail Content -->
          <div
            id="cookiePrefAnalyticsContent"
            class="cookie-content-padding"
            v-show="currentView === 'analytics'"
          >
            <div class="cookie-title-left">Analytics</div>
            <div class="cookie-info-section">
              We use analytics to improve our website by better understanding
              how often users visit the site, what pages they visit most, and
              how long they spend on our site. We'll rely on cookies and
              third-party partners to track these actions and behaviors. Click
              on one of the partner names to learn more.
            </div>
            <div class="cookie-vendor-header">Vendors</div>
            <div class="mb-16">
              <div class="cookie-vendor-item">
                <span
                  id="googleAnalyticsLink"
                  class="cookie-vendor-link"
                  @click="showGoogleAnalyticsDetailView"
                  style="cursor: pointer;"
                  >Google Analytics</span
                >
              </div>
            </div>
          </div>

          <!-- Google Analytics Detail Content -->
          <div
            id="cookiePrefGoogleAnalyticsDetailContent"
            class="cookie-content-padding"
            v-show="currentView === 'googleAnalytics'"
          >
            <div class="mb-12">
              <div class="text-center">
                <div class="cookie-logo-img-lg">
                  <img
                    src="@/assets/googleanalytics.png"
                    height="60"
                    alt="Google Analytics"
                  />
                </div>
              </div>
              <div class="cookie-title-center">Google Analytics</div>
            </div>

            <div class="cookie-info-section">
              <div class="mb-6"><strong>Owned by:</strong> Google Inc.</div>
              <div class="mb-6">
                <strong>Website:</strong>
                <a
                  href="https://marketingplatform.google.com/about/analytics/"
                  target="_blank"
                  class="link-primary"
                  >https://marketingplatform.google.com/about/analytics/</a
                >
              </div>
              <div>
                <strong>Privacy URL:</strong>
                <a
                  href="https://business.safety.google/privacy/"
                  target="_blank"
                  class="link-primary"
                  >https://business.safety.google/privacy/</a
                >
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">In Their Own Words</div>
              <div class="text-body">
                Google Analytics gives you insights into your website traffic
                and marketing effectiveness. We help you buy the right keywords,
                target your best markets, and engage and convert more customers.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">
                What data does this company collect?
              </div>
              <div class="text-body">
                Please note that data collection is not limited to this
                website/application. It is part of a general company-wide data
                collection process, detailed in the company's privacy policy.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb12">Data Collected</div>

              <div class="mb-12">
                <div class="cookie-data-label">Anonymous</div>
                <div class="text-body">
                  Ad Views, Analytics, Browser Information, Cookie Data,
                  Date/Time, Demographic Data, Hardware/Software Type,
                  Interaction Data, Page Views, Serving Domains
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Pseudonymous</div>
                <div class="text-body">
                  Clickstream Data, Device ID (EU PII), IP Address, Location
                  Based Data, Search History
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">PII</div>
                <div class="text-body">
                  Email Address, Login, Name, Phone Number
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Sensitive</div>
                <div class="text-body">Financial Information</div>
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">Data Sharing</div>
              <div class="text-body">
                Aggregate data is shared with 3rd parties., Anonymous data is
                shared with 3rd parties., PII data is shared with 3rd parties.,
                Sensitive data is shared with 3rd parties.
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Retention</div>
              <div class="text-body">
                As long as necessary to fulfill a business need or as required
                by law.
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Use</div>
              <div class="text-body">
                Ad Serving, Ad Targeting, Analytics/Measurement, Content
                Customization, Cross Device Tracking, Optimization
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Storage</div>
              <div class="text-body">
                We maintain servers around the world and your information may be
                processed on servers located outside of the country where you
                live.
              </div>
            </div>
          </div>

          <!-- Essential Detail Content -->
          <div
            id="cookiePrefEssentialContent"
            class="cookie-content-padding"
            v-show="currentView === 'essential'"
          >
            <div class="cookie-title-left">Essential</div>
            <div class="cookie-info-section">
              EssentialRequired We rely on cookies, javascript, and other web
              technologies to serve key—or essential—elements on the site. This
              may include things like your language preferences or server-based
              cookies intended to keep our site running and operational. If
              disabled, your site experience will likely be impacted.
            </div>
            <div class="cookie-vendor-header">Vendors</div>
            <div class="mb-16">
              <div class="cookie-vendor-item">
                <span class="cookie-vendor-item-full">
                  <span
                    id="akamaiTechnologiesLink"
                    class="cookie-vendor-link"
                    @click="showAkamaiDetailView"
                    style="cursor: pointer;"
                    >Akamai Technologies</span
                  >
                  <span class="cookie-required-label-ml">Required</span>
                </span>
              </div>
              <div class="cookie-vendor-item">
                <span class="cookie-vendor-item-full">
                  <span
                    id="fontAwesomeLink"
                    class="cookie-vendor-link"
                    @click="showFontAwesomeDetailView"
                    style="cursor: pointer;"
                    >Font Awesome</span
                  >
                  <span class="cookie-required-label-ml">Required</span>
                </span>
              </div>
              <div class="cookie-vendor-item">
                <span class="cookie-vendor-item-full">
                  <span
                    id="googleFontsLink"
                    class="cookie-vendor-link"
                    @click="showGoogleFontsDetailView"
                    style="cursor: pointer;"
                    >Google Fonts</span
                  >
                  <span class="cookie-required-label-ml">Required</span>
                </span>
              </div>
              <div class="cookie-vendor-item">
                <span class="cookie-vendor-item-full">
                  <span
                    id="googleRecaptchaLink"
                    class="cookie-vendor-link"
                    @click="showGoogleRecaptchaDetailView"
                    style="cursor: pointer;"
                    >Google reCAPTCHA</span
                  >
                  <span class="cookie-required-label-ml">Required</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Akamai Technologies Detail Content -->
          <div
            id="cookiePrefAkamaiDetailContent"
            class="cookie-content-padding"
            v-show="currentView === 'akamai'"
          >
            <div class="mb-12">
              <div class="text-center">
                <div class="cookie-logo-img-lg">
                  <img src="@/assets/akamai.png" height="60" alt="Akamai" />
                </div>
              </div>
              <div class="cookie-title-center">Akamai Technologies</div>
            </div>

            <div class="cookie-info-section">
              <div class="mb-6">
                <strong>Website:</strong>
                <a
                  href="https://www.akamai.com/"
                  target="_blank"
                  class="link-primary"
                  >https://www.akamai.com/</a
                >
              </div>
              <div>
                <strong>Privacy URL:</strong>
                <a
                  href="https://www.akamai.com/legal/privacy-statement"
                  target="_blank"
                  class="link-primary"
                  >https://www.akamai.com/legal/privacy-statement</a
                >
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">In Their Own Words</div>
              <div class="text-body">
                Akamai is the global leader in Content Delivery Network (CDN)
                services, making the Internet fast, reliable and secure for its
                customers. The company's advanced web performance, mobile
                performance, cloud security and media delivery solutions are
                revolutionizing how businesses optimize consumer, enterprise and
                entertainment experiences for any device, anywhere.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">
                What data does this company collect?
              </div>
              <div class="text-body">
                Please note that data collection is not limited to this
                website/application. It is part of a general company-wide data
                collection process, detailed in the company's privacy policy.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb12">Data Collected</div>

              <div class="mb-12">
                <div class="cookie-data-label">Anonymous</div>
                <div class="text-body">
                  Ad Views, Analytics, Browser Information, Cookie Data,
                  Date/Time, Demographic Data, Hardware/Software Type,
                  Interaction Data, Internet Service Provider, Page Views,
                  Serving Domains
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Pseudonymous</div>
                <div class="text-body">
                  Clickstream Data, IP Address, Location Based Data, Search
                  History
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">PII</div>
                <div class="text-body">
                  Address, Email Address, Login, Name, Phone Number
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Sensitive</div>
                <div class="text-body">Financial Information</div>
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Sharing</div>
              <div class="text-body">
                Data is shared with 3rd parties (specifics undisclosed).
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Retention</div>
              <div class="text-body">2-3 Years</div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Use</div>
              <div class="text-body">
                Ad Serving, Ad Targeting, Analytics/Measurement, Content
                Customization, Optimization
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Storage</div>
              <div class="text-body mb-8">
                Data will be collected via servers hosted in Amazon Web Services
                Inc.'s locations that are closest to the website visitor's
                location and will be transferred and stored on Adobe servers in
                the USA.
              </div>
              <a
                id="readMoreAkamaiLink"
                href="https://cop.evidon.com/companies/akamai-technologies"
                target="_blank"
                class="cookie-readmore-link"
                >Read more about Akamai Technologies &gt;</a
              >
            </div>
          </div>

          <!-- Font Awesome Detail Content -->
          <div
            id="cookiePrefFontAwesomeDetailContent"
            class="cookie-content-padding"
            v-show="currentView === 'fontAwesome'"
          >
            <div class="mb-12">
              <div class="text-center">
                <div class="cookie-logo-img-lg">
                  <img
                    src="@/assets/font-awesome.jpeg"
                    height="60"
                    alt="font awesome"
                  />
                </div>
              </div>
              <div class="cookie-title-center">Font Awesome</div>
            </div>

            <div class="cookie-info-section">
              <div class="mb-6">
                <strong>Website:</strong>
                <a
                  href="https://fontawesome.com/"
                  target="_blank"
                  class="link-primary"
                  >https://fontawesome.com/</a
                >
              </div>
              <div>
                <strong>Privacy URL:</strong>
                <a
                  href="https://fontawesome.com/privacy"
                  target="_blank"
                  class="link-primary"
                  >https://fontawesome.com/privacy</a
                >
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">In Their Own Words</div>
              <div class="text-body">
                Get vector icons and social logos on your website with Font
                Awesome, the web's most popular icon set and toolkit.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">
                What data does this company collect?
              </div>
              <div class="text-body">
                Please note that data collection is not limited to this
                website/application. It is part of a general company-wide data
                collection process, detailed in the company's privacy policy.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb12">Data Collected</div>

              <div class="mb-12">
                <div class="cookie-data-label">Anonymous</div>
                <div class="text-body">
                  Analytics, Internet Service Provider, Page Views
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">PII</div>
                <div class="text-body">
                  Email Address, Login, Name
                </div>
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Sharing</div>
              <div class="text-body">
                Data is shared with 3rd parties (specifics undisclosed).
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Retention</div>
              <div class="text-body">
                As long as necessary to fulfill a business need or as required
                by law.
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Use</div>
              <div class="text-body">
                Content Customization
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Storage</div>
              <div class="text-body mb-8">
                Data will be collected via servers hosted in Amazon Web Services
                Inc.'s locations that are closest to the website visitor's
                location and will be transferred and stored on Adobe servers in
                the USA.
              </div>
              <a
                href="https://fontawesome.com/privacy"
                target="_blank"
                class="cookie-readmore-link"
                >Read more about Font Awesome &gt;</a
              >
            </div>
          </div>

          <!-- Google Fonts Detail Content -->
          <div
            id="cookiePrefGoogleFontsDetailContent"
            class="cookie-content-padding"
            v-show="currentView === 'googleFonts'"
          >
            <div class="mb-12">
              <div class="text-center">
                <div class="cookie-logo-img-lg">
                  <img
                    src="@/assets/google-fonts.jpeg"
                    height="60px"
                    alt="Google Fonts"
                  />
                </div>
              </div>
              <div class="cookie-title-center">Google Fonts</div>
            </div>

            <div class="cookie-info-section">
              <div class="mb-6">
                <strong>Website:</strong>
                <a
                  href="https://fonts.google.com/"
                  target="_blank"
                  class="link-primary"
                  >https://fonts.google.com/</a
                >
              </div>
              <div>
                <strong>Privacy URL:</strong>
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  class="link-primary"
                  >https://policies.google.com/privacy</a
                >
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">In Their Own Words</div>
              <div class="text-body">
                Google Fonts is a library of 1,000+ open source font families
                and APIs for convenient use via CSS and Android. The service is
                free to use and available for all websites.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">
                What data does this company collect?
              </div>
              <div class="text-body">
                Please note that data collection is not limited to this
                website/application. It is part of a general company-wide data
                collection process, detailed in the company's privacy policy.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb12">Data Collected</div>

              <div class="mb-12">
                <div class="cookie-data-label">Anonymous</div>
                <div class="text-body">
                  Analytics, Browser Information, Cookie Data, Date/Time, Page
                  Views
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Pseudonymous</div>
                <div class="text-body">
                  IP Address, Location Based Data
                </div>
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Sharing</div>
              <div class="text-body">
                Data is shared with 3rd parties (specifics undisclosed).
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Retention</div>
              <div class="text-body">
                As long as necessary to fulfill a business need or as required
                by law.
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Use</div>
              <div class="text-body">
                Content Customization
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Storage</div>
              <div class="text-body mb-8">
                Data will be collected via servers hosted in Amazon Web Services
                Inc.'s locations that are closest to the website visitor's
                location and will be transferred and stored on Adobe servers in
                the USA.
              </div>
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                class="cookie-readmore-link"
                >Read more about Google Fonts &gt;</a
              >
            </div>
          </div>

          <!-- Google reCAPTCHA Detail Content -->
          <div
            id="cookiePrefGoogleRecaptchaDetailContent"
            class="cookie-content-padding"
            v-show="currentView === 'googleRecaptcha'"
          >
            <div class="mb-12">
              <div class="text-center">
                <div class="cookie-logo-img-lg">
                  <img
                    src="@/assets/google-recaptcha.png"
                    height="60"
                    alt="Google reCAPTCHA"
                  />
                </div>
              </div>
              <div class="cookie-title-center">Google reCAPTCHA</div>
            </div>

            <div class="cookie-info-section">
              <div class="mb-6">
                <strong>Website:</strong>
                <a
                  href="https://www.google.com/recaptcha/"
                  target="_blank"
                  class="link-primary"
                  >https://www.google.com/recaptcha/</a
                >
              </div>
              <div>
                <strong>Privacy URL:</strong>
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  class="link-primary"
                  >https://policies.google.com/privacy</a
                >
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">In Their Own Words</div>
              <div class="text-body">
                reCAPTCHA is a free service from Google that protects your
                website from spam and abuse. reCAPTCHA uses an advanced risk
                analysis engine and adaptive challenges to keep automated
                software from engaging in abusive activities on your site.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb8">
                What data does this company collect?
              </div>
              <div class="text-body">
                Please note that data collection is not limited to this
                website/application. It is part of a general company-wide data
                collection process, detailed in the company's privacy policy.
              </div>
            </div>

            <div class="mb-18">
              <div class="cookie-subtitle-mb12">Data Collected</div>

              <div class="mb-12">
                <div class="cookie-data-label">Anonymous</div>
                <div class="text-body">
                  Analytics, Browser Information, Cookie Data, Date/Time,
                  Hardware/Software Type, Interaction Data, Page Views
                </div>
              </div>

              <div class="mb-12">
                <div class="cookie-data-label">Pseudonymous</div>
                <div class="text-body">
                  IP Address, Location Based Data
                </div>
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Sharing</div>
              <div class="text-body">
                Data is shared with 3rd parties (specifics undisclosed).
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Retention</div>
              <div class="text-body">
                As long as necessary to fulfill a business need or as required
                by law.
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Use</div>
              <div class="text-body">
                Content Customization, Optimization
              </div>
            </div>

            <div class="mb-12">
              <div class="cookie-data-label-mb4">Data Storage</div>
              <div class="text-body mb-8">
                Data will be collected via servers hosted in Amazon Web Services
                Inc.'s locations that are closest to the website visitor's
                location and will be transferred and stored on Adobe servers in
                the USA.
              </div>
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                class="cookie-readmore-link"
                >Read more about Google reCAPTCHA &gt;</a
              >
            </div>
          </div>
        </div>

        <!-- Error Alert (above buttons) -->
        <div class="cookie-alert-container">
          <div
            id="cookiePrefUnsaved"
            class="cookie-prefs-alert"
            v-show="cookiePrefsDirty && showUnsavedAlert"
          >
            <span class="icon">⚠</span>You currently have unsaved changes
          </div>
        </div>

        <!-- Buttons Group (fixed bottom) -->
        <div id="cookiePrefBtnsGroup" class="cookie-buttons-group">
          <button
            v-if="modalType === 'banner' && cookiePrefsDirty"
            id="cookiePrefSaveBtn"
            @click="savePreferences"
          >
            Save Preferences
          </button>
          <button
            v-if="modalType === 'banner' && !cookiePrefsDirty"
            id="cookiePrefOptOutBtn"
            @click="optOut"
          >
            Opt Out
          </button>
          <button
            v-if="modalType === 'footer'"
            id="cookiePrefSaveBtn"
            @click="savePreferences"
          >
            Save Preferences
          </button>
          <button id="cookiePrefCancelBtn" @click="cancelPreferences">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CookieConsentTool",
  data() {
    return {
      showBanner: false,
      showModal: false,
      currentView: "main", // main, analytics, googleAnalytics, essential, akamai, fontAwesome, googleFonts, googleRecaptcha
      modalType: "footer", // 'banner' or 'footer'
      cookiePrefs: {
        analytics: false
      },
      initialPrefsOnOpen: null,
      cookiePrefsDirty: false,
      showUnsavedAlert: false,
      privacyUrl: "https://www.lenovo.com/us/en/privacy/",
      CONSENT_BANNER_KEY: "lid_cookie_consent",
      CONSENT_BANNER_SESSION_KEY: "lid_cookie_consent_session",
      COOKIE_PREFS_KEY: "cookie_preferences"
    };
  },
  computed: {
    isEnglish() {
      return (
        this.$i18n.locale === "en_US" ||
        (this.$i18n.locale && this.$i18n.locale.startsWith("en"))
      );
    }
  },
  mounted() {
    if (this.isEnglish) {
      this.initCookieConsent();
    }
    this.updateBannerWidth();
    window.addEventListener('resize', this.updateBannerWidth);
  },
  watch: {
    "$i18n.locale"() {
      if (this.isEnglish) {
        this.initCookieConsent();
      } else {
        this.showBanner = false;
        this.showModal = false;
        this.emitBannerState();
      }
    },
    showBanner(newVal) {
      this.emitBannerState();
      if (newVal) {
        // 横幅显示时更新宽度
        this.$nextTick(() => {
          this.updateBannerWidth();
        });
      }
    }
  },
  methods: {
    initCookieConsent() {
      // 检查是否应该显示横幅
      if (this.shouldShowBanner()) {
        this.showBanner = true;
        this.setBannerFlag();
        // 横幅显示后更新宽度
        this.$nextTick(() => {
          this.updateBannerWidth();
        });
      }

      // 从localStorage读取偏好设置
      this.loadPreferences();

      // 根据偏好设置加载/卸载Google Analytics
      if (this.cookiePrefs.analytics) {
        this.loadGoogleAnalytics();
      } else {
        this.unloadGoogleAnalytics();
      }

      // 监听自定义事件
      window.addEventListener(
        "cookiePreferencesChanged",
        this.handlePreferencesChanged
      );

      // 发出初始横幅状态
      this.emitBannerState();
    },
    shouldShowBanner() {
      try {
        const hasLocalStorageFlag =
          localStorage.getItem(this.CONSENT_BANNER_KEY) === "done";
        const hasSessionStorageFlag =
          sessionStorage.getItem(this.CONSENT_BANNER_SESSION_KEY) === "done";

        if (hasLocalStorageFlag) {
          return false;
        }

        if (hasSessionStorageFlag) {
          return false;
        }

        return true;
      } catch (e) {
        return true;
      }
    },
    setBannerFlag() {
      try {
        localStorage.setItem(this.CONSENT_BANNER_KEY, "done");
        sessionStorage.setItem(this.CONSENT_BANNER_SESSION_KEY, "done");
      } catch (e) {}
    },
    loadPreferences() {
      try {
        const st = localStorage.getItem(this.COOKIE_PREFS_KEY);
        if (st) {
          const parsed = JSON.parse(st);
          this.cookiePrefs = { analytics: parsed.analytics === true };
        }
      } catch (e) {
        this.cookiePrefs = { analytics: false };
      }
    },
    savePreferences() {
      try {
        localStorage.setItem(
          this.COOKIE_PREFS_KEY,
          JSON.stringify(this.cookiePrefs)
        );
      } catch (e) {}

      // 触发自定义事件
      window.dispatchEvent(
        new CustomEvent("cookiePreferencesChanged", {
          detail: { preferences: this.cookiePrefs }
        })
      );

      // 根据保存的状态加载/卸载Google Analytics
      if (this.cookiePrefs.analytics) {
        this.loadGoogleAnalytics();
      } else {
        this.unloadGoogleAnalytics();
      }

      // 清除脏状态
      this.cookiePrefsDirty = false;
      this.showUnsavedAlert = false;

      // 关闭弹窗和横幅
      this.showModal = false;
      this.closeBannerPersist();
    },
    cancelPreferences() {
      // 恢复到打开时的初始状态
      if (this.initialPrefsOnOpen) {
        this.cookiePrefs = {
          analytics: this.initialPrefsOnOpen.analytics === true
        };
      }

      // 恢复localStorage
      try {
        localStorage.setItem(
          this.COOKIE_PREFS_KEY,
          JSON.stringify(this.cookiePrefs)
        );
      } catch (e) {}

      // 触发自定义事件
      window.dispatchEvent(
        new CustomEvent("cookiePreferencesChanged", {
          detail: { preferences: this.cookiePrefs }
        })
      );

      // 根据恢复的状态加载/卸载Google Analytics
      if (this.cookiePrefs.analytics) {
        this.loadGoogleAnalytics();
      } else {
        this.unloadGoogleAnalytics();
      }

      // 清除脏状态
      this.cookiePrefsDirty = false;
      this.showUnsavedAlert = false;

      // 关闭弹窗和横幅
      this.showModal = false;
      this.closeBannerPersist();
    },
    optOut() {
      this.cookiePrefsDirty = false;
      this.showUnsavedAlert = false;
      this.showModal = false;
      this.closeBannerPersist();
    },
    agreeAllAndCloseBanner() {
      this.setBannerFlag();
      this.cookiePrefs = { analytics: true };
      try {
        localStorage.setItem(
          this.COOKIE_PREFS_KEY,
          JSON.stringify(this.cookiePrefs)
        );
      } catch (e) {}

      // 触发自定义事件
      window.dispatchEvent(
        new CustomEvent("cookiePreferencesChanged", {
          detail: { preferences: this.cookiePrefs }
        })
      );

      this.loadGoogleAnalytics();
      this.closeBannerPersist();
    },
    rejectAllAndCloseBanner() {
      this.setBannerFlag();
      this.cookiePrefs = { analytics: false };
      try {
        localStorage.setItem(
          this.COOKIE_PREFS_KEY,
          JSON.stringify(this.cookiePrefs)
        );
      } catch (e) {}

      // 触发自定义事件
      window.dispatchEvent(
        new CustomEvent("cookiePreferencesChanged", {
          detail: { preferences: this.cookiePrefs }
        })
      );

      this.unloadGoogleAnalytics();
      this.closeBannerPersist();
    },
    closeBannerPersist() {
      this.setBannerFlag();
      this.showBanner = false;
      this.emitBannerState();
    },
    emitBannerState() {
      // 发出横幅状态变化事件
      this.$root.$emit('cookieBannerStateChanged', this.showBanner);
    },
    openCookieConsent() {
      // 如果是从banner打开的，先关闭横幅
      if (this.showBanner) {
        this.closeBannerPersist();
        this.modalType = "banner";
      } else {
        this.modalType = "footer";
      }

      // 保存打开时的初始状态
      this.initialPrefsOnOpen = JSON.parse(JSON.stringify(this.cookiePrefs));

      // 重置视图和状态
      this.currentView = "main";
      this.cookiePrefsDirty = false;
      this.showUnsavedAlert = false;

      this.showModal = true;
    },
    handleCloseClick() {
      if (this.cookiePrefsDirty) {
        this.showUnsavedAlert = true;
      } else {
        this.showModal = false;
      }
    },
    handleOverlayClick() {
      // 点击overlay不关闭，必须点击按钮
    },
    closeModal() {
      this.showModal = false;
    },
    onSwitchChange() {
      this.cookiePrefsDirty = true;

      // 保存临时状态
      try {
        localStorage.setItem(
          this.COOKIE_PREFS_KEY,
          JSON.stringify(this.cookiePrefs)
        );
      } catch (e) {}

      // 触发自定义事件
      window.dispatchEvent(
        new CustomEvent("cookiePreferencesChanged", {
          detail: { preferences: this.cookiePrefs }
        })
      );

      // 根据analytics开关状态动态加载/卸载Google Analytics
      if (this.cookiePrefs.analytics) {
        this.loadGoogleAnalytics();
      } else {
        this.unloadGoogleAnalytics();
      }
    },
    showMainView() {
      this.currentView = "main";
    },
    showAnalyticsView() {
      this.currentView = "analytics";
    },
    showGoogleAnalyticsDetailView() {
      this.currentView = "googleAnalytics";
    },
    showEssentialView() {
      this.currentView = "essential";
    },
    showAkamaiDetailView() {
      this.currentView = "akamai";
    },
    showFontAwesomeDetailView() {
      this.currentView = "fontAwesome";
    },
    showGoogleFontsDetailView() {
      this.currentView = "googleFonts";
    },
    showGoogleRecaptchaDetailView() {
      this.currentView = "googleRecaptcha";
    },
    loadGoogleAnalytics() {
      // 如果已经加载，则不重复加载
      if (window.gaLoaded) return;

      // 动态加载GA4脚本
      const ga4Script = document.createElement("script");
      ga4Script.async = true;
      ga4Script.src =
        "https://www.googletagmanager.com/gtag/js?id=G-LMWHJ8QP3E";
      ga4Script.id = "ga4-script";
      document.head.appendChild(ga4Script);

      // 初始化gtag
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
      }
      ga4Script.onload = function() {
        if (typeof window.gtag === "function") {
          window.gtag("js", new Date());
          window.gtag("config", "G-LMWHJ8QP3E");
          window.gaLoaded = true;
        }
      };
      ga4Script.onerror = function() {
        console.warn("Google Analytics script failed to load");
        window.gaLoaded = false;
      };

      // 动态加载GTM脚本
      (function(w, d, s, l, i) {
        if (document.getElementById("gtm-script")) return;
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        const f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.id = "gtm-script";
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", "GTM-W5CFR6P");

      // 添加GTM noscript iframe
      if (!document.getElementById("gtm-noscript")) {
        const noscript = document.createElement("noscript");
        noscript.id = "gtm-noscript";
        const iframe = document.createElement("iframe");
        iframe.src = "https://www.googletagmanager.com/ns.html?id=GTM-W5CFR6P";
        iframe.height = "0";
        iframe.width = "0";
        iframe.style.cssText = "display:none;visibility:hidden";
        noscript.appendChild(iframe);
        document.body.appendChild(noscript);
      }
    },
    unloadGoogleAnalytics() {
      // 移除GA4脚本
      const ga4Script = document.getElementById("ga4-script");
      if (ga4Script) {
        ga4Script.remove();
      }

      // 移除GTM脚本
      const gtmScript = document.getElementById("gtm-script");
      if (gtmScript) {
        gtmScript.remove();
      }

      // 移除GTM noscript
      const gtmNoscript = document.getElementById("gtm-noscript");
      if (gtmNoscript) {
        gtmNoscript.remove();
      }

      window.gaLoaded = false;
    },
    handlePreferencesChanged(event) {
      if (event.detail && event.detail.preferences) {
        this.cookiePrefs = { ...event.detail.preferences };
      }
    },
    updateBannerWidth() {
      this.$nextTick(() => {
        // 计算滚动条宽度
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const banner = document.getElementById('cookieConsentBanner');
        if (banner) {
          banner.style.width = `calc(100vw - ${scrollbarWidth}px)`;
        }
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener(
      "cookiePreferencesChanged",
      this.handlePreferencesChanged
    );
    window.removeEventListener('resize', this.updateBannerWidth);
  }
};
</script>

<style scoped lang="scss">
.cookie-switch {
  position: relative;
  width: 40px;
  height: 24px;
  display: inline-block;
}
.cookie-switch input {
  width: 40px;
  height: 24px;
  opacity: 0;
  position: absolute;
  left: 0;
  top: 0;
  margin: 0;
  cursor: pointer;
}
.cookie-switch label {
  display: block;
  height: 24px;
  width: 40px;
  border-radius: 12px;
  background: #e3e9f3;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}
.cookie-switch input:checked + label {
  background: #4994eb;
}
.cookie-switch label:after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px #3f3d3d;
  transition: left 0.19s;
}
.cookie-switch input:checked + label:after {
  left: 19px;
  background: #fff;
}
#cookieConsentToolLink{
  text-decoration: underline!important;
}
.cookie-alert-container {
  padding: 13px 25px 0 25px;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-height: 0;
}
.cookie-prefs-alert {
  width: 100%;
  display: inline-flex;
  background: #e63946;
  color: #fff;
  font-weight: 600;
  padding: 8px 18px 8px 28px;
  border-radius: 4px;
  font-size: 15px;
  align-items: center;
  margin: 0;
  position: relative;
  border: 1.5px solid #e63946;
}
.cookie-prefs-alert .icon {
  position: absolute;
  left: 6px;
  top: 4px;
  font-size: 20px;
  font-weight: bold;
}
.link-primary {
  color: #345adb;
  text-decoration: underline;
  cursor: pointer;
}
#cookieConsentBanner .link-primary {
  font-size: 14px;
  font-weight: 700;
}
.cookie-consent-link {
  cursor: pointer;
}
.cookie-content-padding {
  padding: 32px 30px 24px 30px;
}
.text-center {
  text-align: center;
}
.mb-17 {
  margin-bottom: 17px;
}
.mb-16 {
  margin-bottom: 16px;
}
.mb-18 {
  margin-bottom: 18px;
}
.mb-12 {
  margin-bottom: 12px;
}
.mb-6 {
  margin-bottom: 6px;
}
.mb-4 {
  margin-bottom: 4px;
}
.mb-8 {
  margin-bottom: 8px;
}
.mb-9 {
  margin-bottom: 9px;
}
.mb-10 {
  margin-bottom: 10px;
}
.text-body {
  color: #444;
  font-size: 14px;
  line-height: 1.75;
}
.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cookie-banner {
  position: fixed;
  bottom: 30px;
  left: 0;
  background: #f6f9fc;
  border-bottom: 1px solid #b8b2b2;
  padding: 16px 50px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  z-index: 100;
  color: #666;
}
.cookie-banner-container {
  position: relative;
  padding-right: 28px;
  box-sizing: border-box;
  max-height: 200px;
  overflow: auto;
}
.cookie-banner-container > span {
  font-size: 14px;
  text-align: left;
  color: #333;
  display: inline-block;
  line-height: 25px;
}
.cookie-banner-close {
  position: absolute;
  right: 15px;
  top: 5px;
  border: 0;
  background: transparent;
  font-size: 30px;
  color: #8c8585;
  cursor: pointer;
  z-index: 101;
}
.cookie-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
}
.cookie-modal {
  background: #fff;
  border-radius: 8px;
  max-width: 900px;
  width: 80%;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  margin: auto;
  position: relative;
  overflow: hidden;
}
.cookie-modal-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.cookie-modal-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 24px;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
  background: #fff;
  position: relative;
  z-index: 1;
}
.cookie-modal-topbar-link {
  color: #345adb;
  text-decoration: underline;
  cursor: pointer;
  font-size: 15px;
}
.cookie-modal-topbar-close {
  background: none;
  border: 0;
  font-size: 23px;
  line-height: 1;
  color: #000;
  cursor: pointer;
}
.cookie-pref-main-content {
  padding: 32px 30px 24px 30px;
  position: relative;
}
.cookie-logo-img {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
}
.cookie-logo-img-lg {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}
.cookie-title-main {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 20px;
}
.cookie-title-left {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
}
.cookie-title-center {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 16px;
}
.cookie-subtitle {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}
.cookie-subtitle-mb8 {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}
.cookie-subtitle-mb12 {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}
.cookie-category-title {
  color: #345adb;
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
}
.cookie-category-description {
  font-size: 14px;
  color: #444;
  line-height: 1.75;
  margin-bottom: 16px;
}
.cookie-category-description-last {
  font-size: 14px;
  color: #444;
  line-height: 1.75;
  margin-bottom: 0;
}
.cookie-required-label {
  color: #666;
  font-size: 14px;
  margin-left: 10px;
}
.cookie-required-label-ml {
  color: #666;
  font-size: 14px;
  margin-left: 10px;
}
.cookie-vendor-header {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}
.cookie-vendor-link {
  color: #345adb;
  text-decoration: underline;
  cursor: pointer;
  font-size: 15px;
}
.cookie-vendor-item {
  margin-bottom: 12px;
}
.cookie-vendor-item-mb9 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
}
.cookie-vendor-item-full {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cookie-data-label {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.cookie-data-label-mb4 {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.cookie-info-section {
  font-size: 14px;
  color: #444;
  line-height: 1.75;
  margin-bottom: 20px;
}
.cookie-info-section-mb16 {
  font-size: 14px;
  color: #444;
  line-height: 1.75;
  margin-bottom: 16px;
}
.cookie-info-section-left {
  font-size: 14px;
  color: #444;
  line-height: 1.75;
  margin-bottom: 20px;
  text-align: left;
}
.cookie-readmore-link {
  color: #345adb;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
}
.cookie-modal-close {
  position: absolute;
  right: 13px;
  top: 9px;
  background: none;
  border: 0;
  font-size: 23px;
  line-height: 1;
  color: #000;
  cursor: pointer;
}
.cookie-buttons-group {
  padding: 15px 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  border-top: 1px solid #e5e5e5;
  background: #fff;
  position: relative;
  z-index: 1;
}
.cookie-buttons-group button {
  background: #324d90;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 7px 19px;
  font-size: 15px;
  cursor: pointer;
}
.cookie-buttons-group button#cookiePrefCancelBtn {
  background: #fff;
  color: #324d90;
  border: 1.5px solid #324d90;
  padding: 6px 19px;
}

@media screen and (max-width: 768px) {
  .cookie-banner-close {
    right: 15px;
  }
  .cookie-modal {
    width: 95%;
    max-height: 95vh;
  }
  .cookie-content-padding {
    padding: 20px 15px 20px 15px;
  }
  .cookie-pref-main-content {
    padding: 20px 15px 20px 15px;
  }
}

@media screen and (max-width: 470px) {
  .cookie-banner-container > span {
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/CookieConsentTool.vue